import { useQuery } from '@tanstack/react-query';
import { Avatar, EmptyState, ListGroup, ListRow, SearchField, Skeleton } from '@uplink/ui';
import { useState } from 'react';
import { errorText, useTRPC } from '../api.ts';
import type { Nav } from '../nav.ts';
import { usePrefs } from '../prefs.tsx';
import { FilterChips, Pager, useDebounced } from './common.tsx';

type Sort = 'recent' | 'balance' | 'new';
const SORTS: { value: Sort; label: string }[] = [
  { value: 'recent', label: 'Недавно заходили' },
  { value: 'balance', label: 'По балансу' },
  { value: 'new', label: 'Новые' },
];

export function AdminUsersScreen({ nav }: { nav: Nav }) {
  const trpc = useTRPC();
  const { usd, dateTime } = usePrefs();
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<Sort>('recent');
  const [offset, setOffset] = useState(0);
  const query = useDebounced(search.trim());
  const users = useQuery({
    ...trpc.admin.users.queryOptions({ search: query, sort, offset }),
    placeholderData: (prev) => prev,
  });

  return (
    <main className="ul-screen">
      <h1 className="ul-screen__title">Пользователи{users.data ? ` · ${users.data.total}` : ''}</h1>
      <SearchField
        value={search}
        onChange={(e) => {
          setSearch(e.target.value);
          setOffset(0);
        }}
        placeholder="Имя, @username или ID"
      />
      <FilterChips
        options={SORTS}
        value={sort}
        onChange={(v) => {
          setSort(v);
          setOffset(0);
        }}
      />

      {users.isPending ? (
        <Skeleton height={320} />
      ) : users.isError ? (
        <div className="ul-error" role="alert">
          {errorText(users.error)}
        </div>
      ) : users.data.rows.length === 0 ? (
        <EmptyState icon="search" title="Никого не нашли" text="Проверьте имя или Telegram ID" />
      ) : (
        <div className={users.isPlaceholderData ? 'ul-results ul-results--stale' : 'ul-results'}>
          <ListGroup>
            {users.data.rows.map((u) => (
              <ListRow
                key={u.id}
                leading={<Avatar src={u.photoUrl} name={u.firstName} size={36} />}
                title={[u.firstName, u.lastName].filter(Boolean).join(' ')}
                subtitle={[
                  u.username ? `@${u.username}` : `ID ${u.telegramId}`,
                  u.servers ? `серверов ${u.servers}` : null,
                  u.domains ? `доменов ${u.domains}` : null,
                  sort === 'new' ? `с ${dateTime(u.createdAt)}` : `был ${dateTime(u.lastSeenAt)}`,
                ]
                  .filter(Boolean)
                  .join(' · ')}
                value={
                  <span className={u.balanceCents > 0 ? 'ul-history-amount--in' : 'ul-muted'}>
                    {usd(u.balanceCents)}
                  </span>
                }
                chevron
                onClick={() => nav.push({ name: 'adminUser', id: u.id })}
              />
            ))}
          </ListGroup>
          <Pager offset={offset} total={users.data.total} onChange={setOffset} />
        </div>
      )}
    </main>
  );
}
